import React, { useState, useMemo, useEffect } from "react";
import { toast } from "../components/ui/use-toast";
import Step1Employees from "./steps/Step1Employees";
import Step2Scheduling from "./steps/Step2Scheduling";
import Step3SchedulingRules from "./steps/Step3SchedulingRules";
import { TopTabs } from "./components/TopTabs";
import {
  fetchConfig,
  saveConfig,
  uploadEmployeeImage,
} from "./services/employees";
import { useSearchParams } from "react-router-dom";
import useEmployeesValidation from "./hooks/useEmployeesValidation";
import useServiceConfigsValidation  from "./hooks/useServiceConfigsValidation";  /* NEW */

/* -------------------------------------------------------------------------- */
/*                                Constants                                   */
/* -------------------------------------------------------------------------- */
export const WEEK_DAYS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

export const SCHED_MODES = {
  EMPLOYEE_MANAGED: "EMPLOYEE_MANAGED",
  BUSINESS_MANAGED: "BUSINESS_MANAGED",
};

/* -------------------------------------------------------------------------- */
/*                              Helper factory                                */
/* -------------------------------------------------------------------------- */
const defaultSchedule = () => ({
  mode: SCHED_MODES.EMPLOYEE_MANAGED,
  employeeManagedConfig: {
    bookingWindowWeeks: 3,
    maxConcurrent: 1,
    grid: WEEK_DAYS.reduce(
      (acc, d) => ({ ...acc, [d]: [{ start: "", end: "" }] }),
      {}
    ),
  },
  businessManagedConfig: {
    uploadEveryNDays: 7,
    rules: [],
    minEmployeesPerDayByWeekday: null,
    timeOffRequestNoticeDays: 0,
  },
});

/* -------------------------------------------------------------------------- */
/*                            MAIN CONFIG PANEL                               */
/* -------------------------------------------------------------------------- */
export default function EmployeeConfigPanel() {
  /* ─────────── Global wizard‑level state ─────────── */
  const [employees, setEmployees] = useState([]); // null ⇒ loading
  const [services, setServices] = useState(null);
  const [serviceConfigs, setServiceConfigs] = useState([]); // Step‑2 state
  const [schedule, setSchedule] = useState(defaultSchedule()); // Step‑3 state
  const { errors: employeeErrors } = useEmployeesValidation(employees);
  const { errors: svcCfgErrors   } = useServiceConfigsValidation(
    serviceConfigs,
    services
  );

  const hasGlobalErrors = employeeErrors.length > 0 || svcCfgErrors.length > 0;   /* NEW */

  /* routing */
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get("business_id");

  /* UI tab index: 1=Employees / 2=Scheduling / 3=Rules */
  const [tab, setTab] = useState(1);

  /* ─────────── Derived helpers ─────────── */
  const hasServiceProvider = useMemo(
    () => employees?.some((e) => e.roleType === "SERVICE_PROVIDER") || false,
    [employees]
  );

  const hasBusinessManagedConfig = serviceConfigs?.some(
    (cfg) =>
      cfg.mode === SCHED_MODES.BUSINESS_MANAGED
  );

const showRulesTab =
  hasServiceProvider &&
  (schedule.mode === SCHED_MODES.BUSINESS_MANAGED ||
   serviceConfigs?.some(cfg => cfg.mode === SCHED_MODES.BUSINESS_MANAGED));

  /* ─────────── Bootstrap data ─────────── */
  useEffect(() => {
    (async () => {
      try {
        const {
          employees: emps,
          services: svcs,
          serviceSchedules,
          schedule: sched,
        } = await fetchConfig(businessId);

        setEmployees(emps.length ? emps : []);
        setServices(svcs);                // ← UNCOMMENT & keep
        setServiceConfigs(serviceSchedules.length ? serviceSchedules : []);
        setSchedule(sched && sched.mode ? sched : defaultSchedule());
      } catch (e) {
        toast({
          title: "Could not load configuration",
          description: e.message,
          variant: "destructive",
        });
        /* fall back to empty local state so the wizard still works offline */
        setEmployees([]);
        setServices([]);
        setServiceConfigs([]);
        setSchedule(defaultSchedule());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─────────── Persist everything ─────────── */
  const saveConfiguration = async () => {
    if (employees.length > 20) {
      toast({
        title: "Too many employees",
        description: "Limit is 20 employees.",
        variant: "destructive",
      });
      return;
    }
    for (const emp of employees) {
      if (!["SERVICE_PROVIDER", "MANAGING_ACCOUNT"].includes(emp.roleType)) {
        toast({
          title: "Invalid role type",
          description: `Employee ${emp.fullName || ""} has an invalid role.`,
          variant: "destructive",
        });
        return;
      }
      if (
        !["USER", "BUSINESS", "CUSTOM"].includes(emp.identityType)
      ) {
        toast({
          title: "Invalid identity type",
          description: `Employee ${emp.fullName || ""} has an invalid identity type.`,
          variant: "destructive",
        });
        return;
      }
    }

    /* avatar uploads */
    const preparedEmployees = await Promise.all(
      employees.map(async (emp) => {
        if (emp.picture instanceof File) {
          console.log(emp.picture);
          try {
            const fileName = await uploadEmployeeImage({
              file: emp.picture,
              businessId,
              employeeId: emp.id,
            });
            return { ...emp, picture: fileName };
          } catch (err) {
            toast({
              title: "Avatar upload failed",
              description: err.message,
              variant: "destructive",
            });
            throw err;
          }
        }
        return emp;
      })
    );

    /* clean schedule according to current mode OR serviceConfigs
       NOTE: previously we only looked at `schedule.mode`. If a service
       schedule was changed to BUSINESS_MANAGED but `schedule.mode` wasn't
       updated, the backend would receive an EMPLOYEE_MANAGED payload and
       therefore business-managed rules (persisted to `employee_schedule_rules`)
       would be dropped. Ensure we treat config as BUSINESS_MANAGED when any
       service schedule is business-managed. */
    const hasBMService = (serviceConfigs || []).some(
      (c) => c.mode === SCHED_MODES.BUSINESS_MANAGED
    );

    const effectiveMode = hasBMService ? SCHED_MODES.BUSINESS_MANAGED : schedule.mode;

    const cleanedSchedule =
      effectiveMode === SCHED_MODES.EMPLOYEE_MANAGED
        ? {
            mode: SCHED_MODES.EMPLOYEE_MANAGED,
            employeeManagedConfig: {
              bookingWindowWeeks:
                schedule.employeeManagedConfig?.bookingWindowWeeks ?? 3,
              maxConcurrent: schedule.employeeManagedConfig?.maxConcurrent ?? 1,
              grid: schedule.employeeManagedConfig?.grid || {},
            },
            businessManagedConfig: null,
          }
        : {
            mode: SCHED_MODES.BUSINESS_MANAGED,
            employeeManagedConfig: null,
            businessManagedConfig: {
              uploadEveryNDays:
                schedule.businessManagedConfig?.uploadEveryNDays ?? 7,
              rules: schedule.businessManagedConfig?.rules || [],
              minEmployeeConfigs:
                schedule.businessManagedConfig?.minEmployeeConfigs ?? [],
              timeOffRequestNoticeDays:
                schedule.businessManagedConfig?.timeOffRequestNoticeDays ?? 0,
            },
          };

    const cleanedServiceSchedules = serviceConfigs.map((cfg) => ({
      id: cfg.id,
      applyToAll: cfg.applyToAll,
      mode: cfg.mode,
      services: cfg.services,
      employeeManagedConfig:
        cfg.mode === SCHED_MODES.EMPLOYEE_MANAGED
          ? {
              bookingWindowWeeks:
                cfg.employeeManagedConfig?.bookingWindowWeeks ?? 3,
              maxConcurrent:
                cfg.employeeManagedConfig?.maxConcurrent ?? 1,
            }
          : null,
      businessManagedConfig:
        cfg.mode === SCHED_MODES.BUSINESS_MANAGED
          ? {
              uploadEveryNDays:
                cfg.businessManagedConfig?.uploadEveryNDays ?? 7,
            }
          : null,
    }));
    // Debug: log payload so server-side persistence issues can be diagnosed
    console.log('Saving configuration payload', {
      employees: preparedEmployees,
      schedule: cleanedSchedule,
      serviceSchedules: cleanedServiceSchedules,
    });
    /* persist */
    try {
      await saveConfig(businessId, {
        employees: preparedEmployees,
        schedule: cleanedSchedule,
        serviceSchedules: cleanedServiceSchedules,
      });
      setEmployees(preparedEmployees.map((e, i) => {
        // keep every other field that may have changed during editing
        return { ...employees[i], picture: e.picture };
      }));

      toast({
        title: "Saved!",
        description: "Configuration stored successfully.",
      });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err.message ?? "Please retry.",
        variant: "destructive",
      });
    }
  };

  /* ---------------------------------------------------------------------- */
  /*                                  UI                                    */
  /* ---------------------------------------------------------------------- */
  return (
    <div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
      {employees === null && <p>Loading…</p>}

      {employees !== null && (
        <>
          <TopTabs
            active={tab}
            onChange={setTab}
            schedulingEnabled={true}
            rulesEnabled={showRulesTab}
          />

          {/* ── Tab 1 – Employees ── */}
          {tab === 1 && (
            <Step1Employees
              businessId={businessId}
              employees={employees}
              setEmployees={setEmployees}
            />
          )}

          {/* ── Tab 2 – Services scheduling ── */}
          {tab === 2 && (
            <Step2Scheduling
              services={services}
              configs={serviceConfigs}
              setConfigs={setServiceConfigs}
              onSave={saveConfiguration}
              hasGlobalErrors={hasGlobalErrors} 
            />
          )}

          {/* ── Tab 3 – Business‑managed rules ── */}
          {tab === 3 && showRulesTab && (
            <Step3SchedulingRules
              employees={employees.filter(e => e.roleType === 'SERVICE_PROVIDER')}
              services={services}
              schedule={schedule}
              setSchedule={setSchedule}
              onSave={saveConfiguration}
              hasGlobalErrors={hasGlobalErrors} 
            />
          )}
        </>
      )}
    </div>
  );
}
